import { Stack, TextField } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import type { AIModel, AIModelAiProvider, AIModelProvider } from '../../core/types/data-models';

export function AIModelAiProvidersPage() {
  const modelsQuery = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });
  const providersQuery = useQuery({
    queryKey: ['ai-model-providers', 'select'],
    queryFn: () => resources.aiModelProviders.list({ pageSize: 500, sort: 'name' }),
  });

  const modelNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of modelsQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [modelsQuery.data]);
  const providerNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of providersQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [providersQuery.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'aiModelId',
        headerName: 'AI model',
        flex: 1,
        minWidth: 200,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : modelNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'aiModelProviderId',
        headerName: 'Provider',
        flex: 1,
        minWidth: 200,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : providerNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'usagePercentage',
        headerName: 'Usage %',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
    ],
    [modelNameById, providerNameById],
  );

  return (
    <CrudPage<AIModelAiProvider>
      title="AI model routes"
      entityName="Route"
      description="Routing distribution per AI model: what share of prompt runs goes to each provider."
      cacheKey={['ai-model-ai-providers']}
      service={resources.aiModelAiProviders}
      columns={columns}
      defaultValues={{ aiModelId: undefined, aiModelProviderId: undefined, usagePercentage: 100 }}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <EntitySelect<AIModel>
            cacheKey={['ai-models', 'select']}
            service={resources.aiModels}
            label="AI model"
            required
            value={values.aiModelId ?? null}
            onChange={(id) => setValues({ ...values, aiModelId: id ?? undefined })}
          />
          <EntitySelect<AIModelProvider>
            cacheKey={['ai-model-providers', 'select']}
            service={resources.aiModelProviders}
            label="Provider"
            required
            value={values.aiModelProviderId ?? null}
            onChange={(id) => setValues({ ...values, aiModelProviderId: id ?? undefined })}
          />
          <TextField
            label="Usage %"
            type="number"
            required
            inputProps={{ min: 0, max: 100, step: 1 }}
            value={values.usagePercentage ?? 0}
            onChange={(e) =>
              setValues({ ...values, usagePercentage: Number(e.target.value) })
            }
            helperText="Share (0–100) of prompt runs for this model routed through this provider."
          />
        </Stack>
      )}
    />
  );
}
